import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import ImageUpload from './ImageUpload';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Star
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudiesPage = () => {
  const { token } = useAuth();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    industry: '',
    description: '',
    image: '',
    is_featured: false,
    order: 0
  });

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (study = null) => {
    if (study) {
      setEditingStudy(study);
      setFormData(study);
    } else {
      setEditingStudy(null);
      setFormData({
        title: '',
        category: '',
        industry: '',
        description: '',
        image: '',
        is_featured: false,
        order: caseStudies.length
      });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingStudy) {
        await axios.put(`${API}/admin/case-studies/${editingStudy.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Updated!', description: 'Case study updated successfully.' });
      } else {
        await axios.post(`${API}/admin/case-studies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Created!', description: 'Case study created successfully.' });
      }
      setModalOpen(false);
      fetchCaseStudies();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save case study.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      await axios.delete(`${API}/admin/case-studies/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Deleted!', description: 'Case study deleted successfully.' });
      fetchCaseStudies();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete case study.', variant: 'destructive' });
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Case Studies</h1>
            <p className="text-gray-500 mt-1">Showcase your successful projects</p>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] hover:from-[#e55a2b] hover:to-[#ff6b35]"
            data-testid="add-case-study-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Case Study
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#ff6b35]" />
          </div>
        ) : caseStudies.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <p className="text-gray-500">No case studies yet. Add your first success story!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((study) => (
              <div key={study.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                {study.image && (
                  <div className="h-40 bg-gray-100">
                    <img src={study.image} alt={study.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      {study.is_featured && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full mb-2">
                          <Star className="w-3 h-3" />
                          Featured
                        </span>
                      )}
                      <h3 className="font-semibold text-gray-900">{study.title}</h3>
                      <p className="text-sm text-[#ff6b35]">{study.category}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openModal(study)} className="p-2 hover:bg-gray-100 rounded-lg">
                        <Pencil className="w-4 h-4 text-gray-500" />
                      </button>
                      <button onClick={() => handleDelete(study.id)} className="p-2 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{study.description}</p>
                  <p className="text-xs text-gray-400 mt-2">{study.industry}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStudy ? 'Edit Case Study' : 'Add Case Study'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="SAP S/4HANA Migration for Company X"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="SAP S/4HANA Migration"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="Healthcare, Finance, etc."
                />
              </div>
              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                label="Cover Image"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the project..."
                  rows={4}
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#ff6b35] focus:ring-[#ff6b35]"
                />
                <label htmlFor="featured" className="text-sm font-medium text-gray-700">Featured case study</label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-[#ff6b35] hover:bg-[#e55a2b]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingStudy ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CaseStudiesPage;
